import Block from '@mui/icons-material/Block';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Downloading from '@mui/icons-material/Downloading';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import Sync from '@mui/icons-material/Sync';
import { ChipProps, SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { ReaderFirmwareUpdate, ReaderFirmwareUpdateStatus } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';

export interface FirmwareUpdateChip {
  label: string;
  color: ChipProps['color'];
  icon: OverridableComponent<SvgIconTypeMap>;
}

interface StatusChipConfig {
  labelKey: keyof Translation;
  color: ChipProps['color'];
  icon: OverridableComponent<SvgIconTypeMap>;
  showsPercentage?: boolean;
}

const statusChipConfigs: Record<ReaderFirmwareUpdateStatus, StatusChipConfig> = {
  [ReaderFirmwareUpdateStatus.Queued]: { labelKey: 'reader_firmware_queued', color: 'info', icon: HourglassEmpty },
  [ReaderFirmwareUpdateStatus.Downloading]: {
    labelKey: 'reader_firmware_downloading',
    color: 'info',
    icon: Downloading,
    showsPercentage: true
  },
  [ReaderFirmwareUpdateStatus.Installing]: {
    labelKey: 'reader_firmware_installing',
    color: 'info',
    icon: Sync,
    showsPercentage: true
  },
  [ReaderFirmwareUpdateStatus.Succeeded]: {
    labelKey: 'reader_firmware_succeeded',
    color: 'success',
    icon: CheckOutlined
  },
  [ReaderFirmwareUpdateStatus.Failed]: { labelKey: 'reader_firmware_failed', color: 'error', icon: CloseOutlined },
  [ReaderFirmwareUpdateStatus.Rejected]: { labelKey: 'reader_firmware_rejected', color: 'error', icon: CloseOutlined },
  [ReaderFirmwareUpdateStatus.TimedOut]: { labelKey: 'reader_firmware_timed_out', color: 'error', icon: CloseOutlined },
  [ReaderFirmwareUpdateStatus.Canceled]: { labelKey: 'reader_firmware_canceled', color: 'default', icon: Block },
  [ReaderFirmwareUpdateStatus.Removed]: { labelKey: 'reader_firmware_removed', color: 'default', icon: Block }
};

const getFirmwareUpdateChip = (
  { status, percentage }: Pick<ReaderFirmwareUpdate, 'status' | 'percentage'>,
  translation: Translation
): FirmwareUpdateChip => {
  const { labelKey, color, icon, showsPercentage } = statusChipConfigs[status];
  const statusLabel = translation[labelKey];
  return { label: showsPercentage ? `${statusLabel} ${percentage}%` : statusLabel, color, icon };
};

export default getFirmwareUpdateChip;
